import { route } from 'preact-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { BackToFirstPage, Pager } from '../../components/ui/Pager.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import {
  type Notification,
  notificationId,
  useNotificationReads,
} from '../../components/ui/notificationRead.js';
import { toRequestedPage } from '../../components/ui/pageParam.js';
import { pageGoneMessage } from '../../components/ui/resultWording.js';
import { useActingIdentity } from '../../hooks/useActingIdentity.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';

/**
 * The envelope `GET /notifications` has always sent
 * (`backend/src/modules/notifications/service.ts:116-129`), transcribed rather than guessed.
 *
 * 084 (gap-list F7): `NotificationBell` declared `total` and never read it, and asked for no page at
 * all — so a renter with more than 20 notifications was shown 20, presented as all of them, and the
 * 21st was unreachable by any route through the app.
 */
interface NotificationsResponse {
  /** One page. **Empty does NOT mean the renter has none** — see `total`. */
  items: Notification[];
  /** Notifications across ALL pages. */
  total: number;
  /** Echoed from the request. The platform does not clamp it — never assume it is <= `pages`. */
  page: number;
  /** `Math.ceil(total / 20)`, so **0** for a renter with none. */
  pages: number;
}

interface NotificationsPageProps {
  path?: string;
  /**
   * The page being asked for, arriving as a **string** from the address —
   * `/account/notifications?page=3`. preact-router parses the query string into route props, and
   * `AuthGuard` — the router's matched child, not this screen — forwards them with
   * `cloneElement(child, routeProps)` (`AuthGuard.tsx:37`).
   *
   * The address is the state: it survives reload, it can be shared and bookmarked, and because the
   * page number ends up in the fetch path, a retry re-requests the page that failed, not page 1.
   */
  page?: string;
}

/**
 * H17: moving pages is a navigation, not a state change.
 *
 * Page 1 is the plain address, so the renter's normal entry point never grows a redundant
 * `?page=1` and the two spellings of "the first page" do not both end up in their history.
 */
function goToPage(n: number): void {
  route(n <= 1 ? '/account/notifications' : `/account/notifications?page=${n}`);
}

export function NotificationsPage({ page }: NotificationsPageProps) {
  const goToSignIn = useSignInRedirect();
  /**
   * US3. The same rule as the bell's, from the same module rather than a second copy — 053's `F-A`
   * and 075's second body are what two copies produce.
   *
   * 🔴 Read state is tracked here rather than refetched. The platform sorts unread first
   * (`read_at: 1`), so asking again after a mark would re-slice and move rows out from under the
   * reader's finger (FR-015). The consequence — a just-read entry can appear on a different page
   * once the renter *does* move — is stated as an edge case and observed at Gate A, not engineered
   * away.
   */
  const { isUnread, markError, open: openEntry } = useNotificationReads();
  const requestedPage = toRequestedPage(page);
  /**
   * 🔴 085 (gap-list F6): scoped to whoever the app is acting as, exactly as the bell is.
   *
   * The impersonation banner renders on this screen, so an administrator can **stop** acting as a
   * renter without leaving it — and before this, the list they were looking at stayed the renter's.
   * One rule, one mechanism, two surfaces.
   */
  const actingIdentity = useActingIdentity();
  const notificationsRead = useFetch<NotificationsResponse>(
    `/notifications?page=${requestedPage}`,
    actingIdentity,
  );
  const { data, status } = notificationsRead;
  const loading = status === 'loading' || status === 'idle';

  return (
    // 137: bounded to exactly the space the shell leaves available (100dvh minus its 132px
    // chrome, unchanged since 134/136) and clipped, not merely floored — the outer page never
    // overflows, so the shell's bell (above this page, outside it) is never displaced by
    // scrolling. Only the region below the header scrolls.
    <div className="page-bound bg-bg px-4 pt-6 flex flex-col">
      <PageHeader title="Notifications" fallback="/account" />
      <div
        data-testid="page-scroll-region"
        className="flex-1 overflow-y-auto overscroll-y-contain pb-6"
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : status !== 'ok' ? (
          /* 064: a refused read used to render as "No notifications yet." */
          <>
            <ResultState
              state={notificationsRead}
              onRetry={notificationsRead.retry}
              onSignIn={goToSignIn}
            />
            {/* H17: a read that failed on page 3 leaves the renter with a retry and nothing leading
              back to the notifications they were just reading. Offered only past the first page,
              where there is somewhere to go back to. */}
            {requestedPage > 1 && (
              <div className="flex justify-center">
                <BackToFirstPage onFirstPage={() => goToPage(1)} />
              </div>
            )}
          </>
        ) : data && !data.items.length && data.total > 0 ? (
          /* H17: an EMPTY SLICE of a non-empty set. `page` is echoed by the platform and never
           clamped down to `pages`, so a stale bookmark or a shrunken history lands here with 30
           notifications and nothing on this page. Falling through to the copy below would tell a
           renter with a full history that they have none: 064's defect, one screen over. */
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-center text-error">{pageGoneMessage('notifications')}</p>
            <BackToFirstPage onFirstPage={() => goToPage(1)} />
          </div>
        ) : !data?.items.length ? (
          <p className="text-center text-gray-400 py-16">No notifications yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {markError && (
              <p role="alert" className="text-sm text-error">
                {markError}
              </p>
            )}
            <ul className="flex flex-col gap-3">
              {data.items.map((n) => (
                <li key={notificationId(n)}>
                  {/* A real button rather than a clickable `li`: this screen is a full page of rows,
                    so the tap target has to be reachable by keyboard as well as by finger. The
                    panel's rows are still bare `li`s and carry biome's `useKeyWithClickEvents`
                    warning for it — pre-existing, and not widened here. */}
                  <button
                    type="button"
                    onClick={() => openEntry(n)}
                    className={`w-full text-left rounded-xl p-4 transition-colors ${
                      isUnread(n) ? 'bg-surface-hi' : 'bg-surface-card'
                    }`}
                  >
                    <p className={`font-semibold ${isUnread(n) ? 'text-text' : 'text-gray-400'}`}>
                      {n.title}
                    </p>
                    <p className="text-sm text-gray-400">{n.message}</p>
                  </button>
                </li>
              ))}
            </ul>
            {/* Rendered only on a successful read, where `pages` is something the platform just said.
              A pager painted from a remembered count beside a failed page would be the app
              asserting what it was not told. `Pager` returns null at `pages <= 1`, so "no dead
              controls on a single page" is inherited, not re-implemented (FR-003). */}
            <Pager page={data.page} pages={data.pages} onPage={goToPage} />
          </div>
        )}
      </div>
    </div>
  );
}
